import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardLayout from "../../Layout/DashboardLayout";
import ConfigTable from "../../Common/Tables/ConfigTable";
import {
  Button,
  Icon,
  PageHeading,
  ConfirmDialog,
  hasPrivilege,
  PRIVILEGES,
  Dropdown,
} from "../../Common/UIComponents";
import {
  fetchEmployees,
  deleteEmployee,
  clearSelectedEmployee,
} from "../../API_Service/Employee_Slice";

// ─────────────────────────────────────────────
// STATS ROW
// ─────────────────────────────────────────────

const StatsRow = ({ rows }) => {
  const total    = rows.length;
  const active   = rows.filter((r) => r.status === "Active").length;
  const inactive = rows.filter((r) => r.status === "Inactive").length;
  const roles    = new Set(rows.map((r) => r.role).filter(Boolean)).size;

  const stats = [
    { label: "Total Associates", value: total,    icon: "group",            iconBg: "bg-[#005b60]/10", iconColor: "text-[#005b60]" },
    { label: "Active",           value: active,   icon: "verified",         iconBg: "bg-[#006fa8]/10", iconColor: "text-[#006fa8]" },
    { label: "Inactive",         value: inactive, icon: "block",            iconBg: "bg-[#dc2626]/10", iconColor: "text-[#dc2626]" },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
      {stats.map((s) => (
        <div key={s.label} className="bg-white p-3.5 rounded-xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/50">
          <p className="text-[#3e494a] text-xs font-bold uppercase tracking-widest mb-1.5">{s.label}</p>
          <div className="flex items-center gap-3">
            <div className={`p-2 ${s.iconBg} rounded-lg ${s.iconColor}`}>
              <span className="material-symbols-outlined text-[20px]">{s.icon}</span>
            </div>
            <h3 className="text-2xl font-black text-[#181c1d]">{s.value}</h3>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────
// EXPORT COLUMNS
// ─────────────────────────────────────────────

const EXPORT_COLUMNS = [
  { key: "code",      title: "Associate Code" },
  { key: "firstName", title: "Name"           },
  { key: "email",     title: "Email"          },
  { key: "phone",     title: "Phone"          },
  { key: "role",      title: "Role"           },
  { key: "status",    title: "Status"         },
];

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

const UserManagementPage = () => {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const location   = useLocation();
  const { user }   = useSelector((state) => state.auth);
  const companyId  = user?.CompanyId || localStorage.getItem("companyId");
  const { list = [] } = useSelector((state) => state.employee);

  const [searchQuery,  setSearchQuery]  = useState("");
  const [roleFilter,   setRoleFilter]   = useState("All Roles");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [currentPage,  setCurrentPage]  = useState(1);
  const [pageSize,     setPageSize]     = useState(10);
  const [editSuccess,  setEditSuccess]  = useState(false);
  const [successMsg,   setSuccessMsg]   = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, row: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteBlocked, setDeleteBlocked] = useState(false);

  useEffect(() => {
    if (companyId) dispatch(fetchEmployees(companyId));
  }, [companyId, dispatch]);

  useEffect(() => {
    if (location.state?.refresh) {
      dispatch(fetchEmployees(companyId));
      if (location.state?.editSuccess) {
        setSuccessMsg(location.state?.isNew ? "Associate Saved successfully." : "Associate Updated successfully.");
        setEditSuccess(true);
        setTimeout(() => setEditSuccess(false), 3000);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.refresh, location.pathname, navigate, dispatch, companyId]);

  // ── Rows ──────────────────────────────────────
  const rows = useMemo(() =>
    list.map((item) => ({
      id:        item.EmployeeID || item.id,
      code:      item.EmpCode || item.EmployeeID,
      firstName: item.EmployeeName || "",
      email:     item.EmailID,
      phone:     item.ContactNumber,
      role:      item.RoleName,
      status:    item.IsActive === 1 ? "Active" : "Inactive",
    }))
  , [list]);

  // Splits a combined role string (e.g. "Admin, Manager") into individual role names
  const parseRoles = (roleStr) =>
    (roleStr || "").split(",").map((r) => r.trim()).filter(Boolean);

  // ── Role options for filter ───────────────────
  const roleOptions = useMemo(() => {
    const roles = new Set();
    rows.forEach((r) => parseRoles(r.role).forEach((role) => roles.add(role)));
    return ["All Roles", ...Array.from(roles).sort()];
  }, [rows]);

  const roleDropdownOptions = useMemo(
    () => roleOptions.map((opt) => ({ label: opt, value: opt })),
    [roleOptions],
  );

  // ── Filtered rows (role filter + status filter + search) ─────
  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((r) => {
      const matchesRole   = roleFilter === "All Roles" || parseRoles(r.role).includes(roleFilter);
      const matchesStatus = statusFilter === "All Status" || r.status === statusFilter;
      const matchesSearch = !q ||
        r.firstName?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        String(r.code ?? "").toLowerCase().includes(q) ||
        String(r.phone ?? "").toLowerCase().includes(q) ||
        r.role?.toLowerCase().includes(q);
      return matchesRole && matchesStatus && matchesSearch;
    });
  }, [rows, searchQuery, roleFilter, statusFilter]);

  const totalPages    = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  // ── Handlers ─────────────────────────────────
  const handleAddUser   = useCallback(() => { dispatch(clearSelectedEmployee()); navigate("/user-management/create"); }, [dispatch, navigate]);
  const handleEdit      = useCallback((row) => navigate(`/user-management/edit/${row.id}`), [navigate]);
  const handleDelete    = useCallback((row) => { setDeleteBlocked(false); setDeleteDialog({ open: true, row }); }, []);
  const handleCancelDelete = useCallback(() => { setDeleteDialog({ open: false, row: null }); setDeleteBlocked(false); }, []);
  const handleSearchChange  = useCallback((e) => { setSearchQuery(e.target.value); setCurrentPage(1); }, []);
  const handlePageSizeChange = useCallback((size) => { setPageSize(size); setCurrentPage(1); }, []);

  const handleConfirmDelete = async () => {
    const { row } = deleteDialog;
    if (!row) return;
    setDeleteLoading(true);
    try {
      await dispatch(deleteEmployee({ empId: row.id })).unwrap();
      setDeleteDialog({ open: false, row: null });
      await dispatch(fetchEmployees(companyId));
      setSuccessMsg("AssociateDeleted successfully.");
      setEditSuccess(true);
      setTimeout(() => setEditSuccess(false), 3000);
    } catch (error) {
      if (error?.mapped) setDeleteBlocked(true);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Columns ───────────────────────────────────
  const columns = useMemo(() => [
    {
      key: "code",
      title: "Associate Code",
      render: (row) => (
        <code className="text-[11px] bg-[#08757C]/10 px-2.5 py-1 rounded-lg text-[#08757C] font-mono font-bold">
          {row.code}
        </code>
      ),
    },
    {
      key: "firstName",
      title: "Name",
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-[#005b60]/10 flex items-center justify-center text-[10px] font-bold text-[#005b60] flex-shrink-0">
            {row.firstName?.charAt(0)?.toUpperCase()}
          </div>
          <span className="text-sm font-medium text-[#181c1d]">{row.firstName}</span>
        </div>
      ),
    },
    {
      key: "email",
      title: "Email",
      render: (row) => <span className="text-sm text-[#3e494a]">{row.email || "—"}</span>,
    },
    {
      key: "phone",
      title: "Phone",
      render: (row) => <span className="text-sm text-[#3e494a]">{row.phone || "—"}</span>,
    },
    {
      key: "role",
      title: "Role",
      render: (row) => {
        const roles = parseRoles(row.role);
        if (roles.length === 0) {
          return (
            <span className="text-xs bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full font-semibold">
              N/A
            </span>
          );
        }
        return (
          <div className="flex flex-wrap gap-1.5 max-w-xs">
            {roles.map((role, idx) => (
              <span
                key={idx}
                className="text-xs bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full font-semibold whitespace-nowrap"
              >
                {role}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: "status",
      title: "Status",
      render: (row) => (
        <span className={`text-[10px] px-3 py-1 rounded-full font-black uppercase tracking-wider ${
          row.status === "Active"
            ? "bg-[#bbf46e]/20 text-[#112000]"
            : "bg-red-100 text-red-700"
        }`}>
          {row.status}
        </span>
      ),
    },
  ], []);

  return (
    <DashboardLayout>
      <div className="px-8 py-4 bg-[#f7fafa] text-[#181c1d] min-h-screen">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-1.5">
          <span>User Management</span>
          <span className="material-symbols-outlined text-[14px]">chevron_right</span>
          <span className="text-[#08757C] font-semibold">Associates</span>
        </nav>

        {/* Header */}
        <div className="flex flex-wrap justify-between items-end mb-2.5 gap-4">
          <PageHeading
            title="Associate Management"
            subtitle="Manage system users, their roles and access permissions."
          />
          {hasPrivilege(PRIVILEGES.EMP_CREATE) && (
            <Button variant="primary" onClick={handleAddUser}>
              <Icon name="add" className="mr-1 text-[20px]" />
              Add New Associate
            </Button>
          )}
        </div>

        {/* Stats */}
        <StatsRow rows={rows} />

        {/* Role + Status filters above table */}
        <div className="flex items-center gap-3 mb-2.5">
          <label className="text-xs font-bold text-[#3e494a] uppercase tracking-widest">Role:</label>
          <div className="w-56">
            <Dropdown
              options={roleDropdownOptions}
              value={roleFilter}
              onChange={(val) => { setRoleFilter(val); setCurrentPage(1); }}
              placeholder="All Roles"
            />
          </div>

          <label className="text-xs font-bold text-[#3e494a] uppercase tracking-widest">Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-1.5 bg-white border border-[#e5e9e9] rounded-lg text-sm text-[#181c1d] focus:outline-none focus:ring-2 focus:ring-[#08757C]"
          >
            <option value="All Status">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        {/* Table */}
        <ConfigTable
          columns={columns}
          rows={paginatedRows}
          onEdit={hasPrivilege(PRIVILEGES.EMP_EDIT) ? handleEdit : undefined}
          onDelete={hasPrivilege(PRIVILEGES.EMP_DELETE) ? handleDelete : undefined}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          allFilteredRows={filteredRows}
          exportColumns={EXPORT_COLUMNS}
          exportFilename="associates"
          emptyText="No associates found."
        />
      </div>

      <ConfirmDialog
        isOpen={deleteDialog.open}
        variant="danger"
        title="Delete Associate"
        message={`Are you sure you want to delete "${deleteDialog.row?.firstName}"? This action cannot be undone.`}
        isLoading={deleteLoading}
        errorMessage={deleteBlocked ? `"${deleteDialog.row?.firstName}" is already mapped to a shift and cannot be deleted.` : null}
        cancelLabel={deleteBlocked ? "OK, Got it" : "Cancel"}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {editSuccess && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold" role="status">
          <span className="material-symbols-outlined text-[20px]">check_circle</span>
          {successMsg}
        </div>
      )}
    </DashboardLayout>
  );
};

export default UserManagementPage;
